import { createApi } from '@reduxjs/toolkit/query/react';

import { TagTypesEnum } from '@enum/tag-types.enum';
import {
  AddIntegrationConfigInterface,
  AddIntegrationEndpointsInterface,
  AddIntegrationMapInterface,
  AddIntegrationSerialNumbersConfigInterface,
  DeleteIntegrationMapInterface,
  DeleteIntegrationScopeInterface,
  IntegrationFieldsInputInterface,
  IntegrationInputInterface,
  IntegrationInterface,
  IntegrationSchemaInterface,
  IntegrationScopeInputInterface,
} from '@interface/integration/integration.interface';
import { ResponseDataInterface } from '@interface/logs/response-data.interface';
import { portalQueryAuth } from '@util/api/portal-query-auth';

export const integrationApi = createApi({
  reducerPath: 'integration-api',
  tagTypes: [TagTypesEnum.Integration],
  baseQuery: portalQueryAuth('/integrations'),
  endpoints: (build) => ({
    loadIntegrations: build.query<ResponseDataInterface<IntegrationInterface[]>, string>({
      query: (applicationId: string) => `/?application=${applicationId}`,
      providesTags: [TagTypesEnum.Integration],
    }),
    getIntegration: build.query<
      ResponseDataInterface<IntegrationInterface>,
      { applicationId: string; serviceId: string }
    >({
      query: ({ applicationId, serviceId }: { applicationId: string; serviceId: string }) =>
        `/${serviceId}?application=${applicationId}`,
    }),
    getIntegrationSchema: build.query<
      ResponseDataInterface<IntegrationSchemaInterface[]>,
      { serviceId: string; type: string }
    >({
      query: ({ serviceId, type }: { serviceId: string; type: string }) => `/${serviceId}/schema/${type}`,
    }),
    saveIntegration: build.mutation<ResponseDataInterface<IntegrationInterface>, IntegrationInputInterface>({
      query: (credentials: IntegrationInputInterface) => ({
        url: `/${credentials.serviceId}`,
        method: 'PUT',
        body: credentials,
      }),
      invalidatesTags: [TagTypesEnum.Integration],
    }),
    addIntegrationScope: build.mutation<ResponseDataInterface<IntegrationInterface>, IntegrationScopeInputInterface>({
      query: (credentials: IntegrationScopeInputInterface) => ({
        url: `/${credentials.serviceId}/application/${credentials.application}/scope/${credentials.type}?account=${credentials.accountId}`,
        method: 'PUT',
        body: credentials.scope,
      }),
      invalidatesTags: [TagTypesEnum.Integration],
    }),
    saveIntegrationScope: build.mutation<ResponseDataInterface<IntegrationInterface>, IntegrationScopeInputInterface>({
      query: (credentials: IntegrationScopeInputInterface) => ({
        url: `/${credentials.serviceId}/application/${credentials.application}/scope/${credentials.type}?account=${credentials.accountId}`,
        method: 'PATCH',
        body: credentials.scope,
      }),
      invalidatesTags: [TagTypesEnum.Integration],
    }),
    updateIntegrationMap: build.mutation<ResponseDataInterface<IntegrationInterface>, AddIntegrationMapInterface>({
      query: ({ serviceId, application, accountId, data }: AddIntegrationMapInterface) => ({
        url: `/${serviceId}/application/${application}/remap?account=${accountId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [TagTypesEnum.Integration],
    }),
    updateIntegrationEndpoints: build.mutation<
      ResponseDataInterface<IntegrationInterface>,
      AddIntegrationEndpointsInterface
    >({
      query: ({ serviceId, application, accountId, data }: AddIntegrationEndpointsInterface) => ({
        url: `/${serviceId}/application/${application}/order-endpoints?account=${accountId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [TagTypesEnum.Integration],
    }),
    updateIntegrationSerialNumbersConfig: build.mutation<
      ResponseDataInterface<IntegrationInterface>,
      AddIntegrationSerialNumbersConfigInterface
    >({
      query: ({ serviceId, application, accountId, data }: AddIntegrationSerialNumbersConfigInterface) => ({
        url: `/${serviceId}/application/${application}/serial-numbers-config?account=${accountId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [TagTypesEnum.Integration],
    }),
    updateIntegrationConfig: build.mutation<ResponseDataInterface<IntegrationInterface>, AddIntegrationConfigInterface>(
      {
        query: ({ serviceId, application, accountId, data }: AddIntegrationConfigInterface) => ({
          url: `/${serviceId}/application/${application}/config?account=${accountId}`,
          method: 'PUT',
          body: data,
        }),
        invalidatesTags: [TagTypesEnum.Integration],
      },
    ),
    updateIntegrationFields: build.mutation<
      ResponseDataInterface<IntegrationInterface>,
      IntegrationFieldsInputInterface
    >({
      query: ({ serviceId, application, type, accountId, data }: IntegrationFieldsInputInterface) => ({
        url: `/${serviceId}/application/${application}/fields/${type}?account=${accountId}`,
        method: 'PATCH',
        body: {
          fields: data,
        },
      }),
      invalidatesTags: [TagTypesEnum.Integration],
    }),
    deleteIntegrationScope: build.mutation<
      ResponseDataInterface<IntegrationInterface>,
      DeleteIntegrationScopeInterface
    >({
      query: (credentials: DeleteIntegrationScopeInterface) => ({
        url: `/${credentials.serviceId}/application/${credentials.application}/scope/${credentials.type}?account=${credentials.accountId}`,
        method: 'DELETE',
        body: credentials.scope,
      }),
      invalidatesTags: [TagTypesEnum.Integration],
    }),
    deleteIntegrationMap: build.mutation<ResponseDataInterface<IntegrationInterface>, DeleteIntegrationMapInterface>({
      query: ({ serviceId, application, accountId, id }: DeleteIntegrationMapInterface) => ({
        url: `/${serviceId}/application/${application}/remap/${id}?account=${accountId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [TagTypesEnum.Integration],
    }),
  }),
});
